import React, { useState } from "react";
import { NodeResizer } from "@xyflow/react";
import { Group, Rows3 } from "lucide-react";

// 片4 分组框（groupFrame）
// 基于 ReactFlow 官方父子节点机制：子节点 parentId = 框 id + extent:'parent'，
// 移动框自动带动子节点（见 NodeCanvas createGroupFrame / onNodeDragStop / handleRemoveFromGroup）。
// 视觉：hairline 边框 + 半透明底色（主题变量），标题栏拖动整组，双击标题重命名，5 色可选。
// 无连接端口（connectionRules 禁入禁出）。

const GROUP_COLORS = {
  blue:   "var(--color-blue-primary)",
  green:  "var(--color-green-primary)",
  purple: "var(--color-purple-primary)",
  orange: "var(--color-warning)",
  pink:   "var(--color-pink-primary)",
};
const DEFAULT_COLOR = "blue";

const GroupFrameNode = ({ data, id, selected }) => {
  const [hovered, setHovered] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [draft, setDraft] = useState("");

  const colorKey = data.groupColor || DEFAULT_COLOR;
  const color = GROUP_COLORS[colorKey] || GROUP_COLORS[DEFAULT_COLOR];
  const title = data.name || "分组";
  const isStoryboardSequence = data.groupRole === "storyboardSequence";
  const HeaderIcon = isStoryboardSequence ? Rows3 : Group;

  const commitTitle = () => {
    setEditingTitle(false);
    const next = draft.trim();
    if (next && next !== title) {
      data.onUpdate?.(id, { ...data, name: next });
    }
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: 12,
        border: `1px solid color-mix(in srgb, ${color} 45%, transparent)`,
        background: `color-mix(in srgb, ${color} 8%, transparent)`,
        boxShadow: selected
          ? `0 0 0 2px color-mix(in srgb, ${color} 35%, transparent)`
          : "none",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* 尺寸调整：选中/hover 时显示手柄；结束后尺寸持久化到 data.frameWidth/frameHeight */}
      <NodeResizer
        isVisible={selected || hovered}
        minWidth={isStoryboardSequence ? 320 : 240}
        minHeight={160}
        lineStyle={{ borderColor: color }}
        handleStyle={{ background: color, width: 8, height: 8, borderRadius: 2 }}
        onResizeEnd={(event, params) => {
          data.onUpdate?.(id, {
            ...data,
            frameWidth: Math.round(params.width),
            frameHeight: Math.round(params.height),
          });
        }}
      />
      {/* 标题栏：拖动此区域移动整组（输入框与色板 nodrag） */}
      <div className="flex items-center gap-1.5 px-2 py-1" style={{ cursor: "grab" }}>
        <HeaderIcon size={11} style={{ color, flexShrink: 0 }} />
        {editingTitle ? (
          <input
            className="nodrag text-2xs bg-transparent outline-none flex-1 min-w-0"
            style={{
              color: "var(--color-text-primary)",
              borderBottom: `1px solid color-mix(in srgb, ${color} 60%, transparent)`,
            }}
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitTitle();
              if (e.key === "Escape") setEditingTitle(false);
            }}
            onDoubleClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="text-2xs font-bold truncate"
            style={{ color, maxWidth: 200 }}
            title="双击重命名分组"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setDraft(title);
              setEditingTitle(true);
            }}
          >
            {title}
          </span>
        )}

        {isStoryboardSequence && !editingTitle && (
          <span
            className="text-2xs shrink-0"
            style={{ color: "var(--color-text-muted)" }}
          >
            {Number(data.shotCount || 0)} 镜 · 纵向
          </span>
        )}

        {/* 5 色可选：hover / 选中时浮出 */}
        {(hovered || selected) && !editingTitle && (
          <div className="nodrag flex items-center gap-1 ml-auto">
            {Object.entries(GROUP_COLORS).map(([key, c]) => (
              <button
                key={key}
                title={`分组配色 ${key}`}
                onClick={(e) => {
                  e.stopPropagation();
                  data.onUpdate?.(id, { ...data, groupColor: key });
                }}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: c,
                  opacity: key === colorKey ? 1 : 0.35,
                  border: key === colorKey ? "1px solid var(--color-text-primary)" : "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(GroupFrameNode);
